import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import type { Program } from '../../../js/api/models/Program';
import { ProviderService } from '../../../js/api/services/ProviderService';
import { ReferralsService } from '../../../js/api/services/ReferralsService';
import { useCurrencyFormatter } from '../../../js/app/hooks/use-currency-formatter';
import { providerQueryKeys } from '../../../js/app/query-keys-factories/providers';
import { Button } from './button';
import { Spinner } from './spinner';
import { Text } from './text';

const SUGGESTED_MESSAGE_TEMPLATE =
  'I came across a physician platform called Sendit I thought you should know about. It connects ' +
  "doctors directly with high-paying, contract jobs. Their tech makes it easy and they're " +
  'transparent on rates and details before you commit. ' +
  'Thought you might find it interesting. [link]';

export interface ProviderReferralContentProps {
  /** When true, data is fetched. Use to avoid fetching when content is not visible. */
  active: boolean;
  /** When present, sharing options (Copy Link, Message, Suggested Message) are hidden. */
  programId?: string;
}

export function ProviderReferralContent({ active, programId }: ProviderReferralContentProps) {
  const [program, setProgram] = useState<Program | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { formatCurrency } = useCurrencyFormatter({ postfix: '', placeholder: '-' });

  const { data: providerForReferral } = useQuery({
    queryKey: providerQueryKeys.currentDetail(),
    queryFn: () => ProviderService.providerCurrentRetrieve(),
    enabled: active,
  });
  const referralUrl = providerForReferral?.referral_url ?? null;

  const suggestedMessage = SUGGESTED_MESSAGE_TEMPLATE.replace('[link]', referralUrl ?? '');

  useEffect(() => {
    if (!active) return;

    setIsLoading(true);
    setError(null);
    setProgram(null);

    const fetchProgram = programId
      ? ReferralsService.referralsProgramsRetrieve({ id: programId })
      : ReferralsService.referralsProgramsList({ current: true }).then(
          (response) => response.results?.[0] ?? null
        );

    fetchProgram
      .then((programData) => setProgram(programData ?? null))
      .catch(() => {
        setError('Unable to load referral program.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [active, programId]);

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(suggestedMessage);
      toast.success('Message copied successfully');
    } catch {
      toast.error('Failed to copy message');
    }
  };

  const handleCopyLink = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const canCopyLink = !!referralUrl;

  const handleMessage = async () => {
    if (!navigator.share) {
      await handleCopyMessage();
      return;
    }

    try {
      await navigator.share({
        title: 'Locums Without the Agency',
        text: suggestedMessage,
      });
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    }
  };

  return (
    <>
      {/* Program section */}
      <div className="tw:mt-2 tw:bg-brand-green-3 tw:p-4 tw:rounded-lg">
        {isLoading ? (
          <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
            <Spinner variant="zinc" />
          </div>
        ) : error ? (
          <div className="tw:py-6 tw:text-center tw:text-zinc-500">{error}</div>
        ) : program ? (
          <>
            <div className="tw:mb-4">
              <p className="tw:text-brand-green-0 tw:font-bold tw:!mb-0">{program.title}</p>
              <Text className="tw:mt-1 tw:text-sm tw:text-zinc-600">{program.description}</Text>
            </div>

            {/* Milestone cards */}
            <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4">
              {program.milestones.slice(0, 2).map((milestone) => (
                <div
                  key={milestone.id}
                  className="tw:rounded-lg tw:border tw:border-zinc-200 tw:p-4 tw:bg-white"
                >
                  <div className="tw:text-xl tw:font-bold tw:text-brand-green-0">
                    {formatCurrency(milestone.value)}
                  </div>
                  <Text className="tw:mt-1 tw:text-xs tw:text-zinc-500">
                    {milestone.description}
                  </Text>
                </div>
              ))}
            </div>

            <p className="tw:text-xs tw:text-zinc-500">
              We do <strong>NOT</strong> spam individuals, sell their data, or share contact lists.
            </p>
          </>
        ) : (
          <div className="tw:py-6 tw:text-center tw:text-zinc-500">
            No referral program available.
          </div>
        )}
      </div>

      {!programId && (
        <>
          {/* Suggested message */}
          <div className="tw:mt-6 tw:rounded-lg tw:bg-zinc-100 tw:p-4">
            <div className="tw:flex tw:items-center tw:justify-between tw:mb-2">
              <span className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-zinc-500">
                Suggested Message
              </span>
              <button
                type="button"
                onClick={handleCopyMessage}
                className="tw:text-sm tw:text-brand-green-0 tw:hover:underline tw:focus:outline-none"
              >
                Copy
              </button>
            </div>
            <Text className="tw:text-sm tw:text-zinc-500">{suggestedMessage}</Text>
          </div>

          {/* Action buttons */}
          <div className="tw:my-6 tw:flex tw:gap-3 tw:justify-center">
            <Button
              color="brand-green-outline"
              className="tw:!p-4 tw:!w-full"
              onClick={handleCopyLink}
              disabled={!canCopyLink}
            >
              <i className="fas fa-fw fa-link" />
              Copy Link
            </Button>
            <Button color="brandgreen1" className="tw:!p-4 tw:!w-full" onClick={handleMessage}>
              <i className="fas fa-fw fa-message-lines" />
              Message
            </Button>
          </div>
        </>
      )}

      {/* Footer */}
      <p className="tw:mt-6 tw:text-xs tw:text-zinc-500">
        Referral credits are automatically tracked. You&apos;ll be credited when your peer completes
        any given program milestone. Once you earn referral credit we will contact you to coordinate
        payment.
      </p>
      <p className="tw:text-xs tw:text-zinc-500 tw:italic">
        <strong>Referral Program Terms Apply.</strong> Credits are subject to eligibility
        verification and program requirements. Sendit reserves the right to withhold, reverse, or
        deny credits for any reason, including suspected fraud or failure to meet program
        requirements. See{' '}
        <a
          href="https://www.senditgigs.com/terms-of-service/"
          target="_blank"
          rel="noopener noreferrer"
        >
          full terms
        </a>
        .
      </p>
    </>
  );
}
